// Per-user data silo namespace (window.backlit.userdata). Backed by the
// data API at /_backlit/userdata{,/{key}}.
//
// The server returns 401 unauthenticated when no user JWT is present (an
// anonymous or signed-out visitor). We pass that through as a
// BacklitError but rewrite the message to name the actual usage
// constraint — the server's "a valid user session cookie is required" is
// correct but not actionable.

import { normalizeBody } from "./body.js";
import { BacklitError } from "./errors.js";
import { getBackend, type BlobResult, type ListEntry, type ListOptions, type ListPage, type BatchEntry, type GetBatchOptions, type PutResult, type PutIfMatchResult } from "./backend.js";
import { validateDataKey, validateDataPrefix } from "./validate.js";
import {
  JSON_CONTENT_TYPE,
  encodeJSON,
  decodeJSON,
  decodeBatchJSONPage,
  carryTags,
  runUpdateJSON,
  type JSONResult,
  type BatchJSONEntry,
  type PutJSONIfMatchResult,
  type UpdateJSONOptions,
} from "./json.js";

export interface UserDataNamespace {
  get(key: string): Promise<BlobResult>;
  put(key: string, body: BodyInit, contentType: string): Promise<PutResult>;
  // putIfMatch is an atomic compare-and-swap against this user's silo.
  // Same contract as data.putIfMatch (see DataNamespace): a mismatch
  // resolves { matched: false, witness } and never throws; empty
  // expectedCrc32c means create-only. Throws unauthenticated for an
  // anonymous / signed-out visitor (no user JWT), like the other
  // userdata.* methods.
  putIfMatch(key: string, body: BodyInit, contentType: string, expectedCrc32c: string): Promise<PutIfMatchResult>;
  delete(key: string): Promise<void>;
  // list returns one bounded page + an opaque `next` cursor (DATA-1),
  // identical contract to data.list. Throws unauthenticated for an
  // anonymous / signed-out visitor (no user JWT).
  list(prefix?: string, opts?: ListOptions): Promise<ListPage<ListEntry>>;

  // getBatch is the value-bearing sibling of list (identical contract to
  // data.getBatch), scoped to this user's silo. Throws unauthenticated for an
  // anonymous / signed-out visitor (no user JWT).
  getBatch(prefix?: string, opts?: GetBatchOptions): Promise<ListPage<BatchEntry>>;

  // JSON convenience — identical contract to data.getJSON / putJSON /
  // putJSONIfMatch, plus the userdata `unauthenticated` rewrite (inherited
  // because these compose the wrapped base methods below).
  getJSON<T = unknown>(key: string): Promise<JSONResult<T>>;
  // getBatchJSON — getBatch with each included value JSON.parsed (identical
  // contract to data.getBatchJSON); inherits the userdata unauthenticated
  // rewrite via the wrapped getBatch below.
  getBatchJSON<T = unknown>(prefix?: string, opts?: GetBatchOptions): Promise<ListPage<BatchJSONEntry<T>>>;
  putJSON(key: string, value: unknown): Promise<PutResult>;
  putJSONIfMatch<T = unknown>(key: string, value: unknown, expectedCrc32c: string): Promise<PutJSONIfMatchResult<T>>;
  // updateJSON — read-modify-write CAS over this user's silo. Same contract
  // as data.updateJSON; throws unauthenticated for an anonymous / signed-out
  // visitor (inherited from the wrapped getJSON/putJSONIfMatch).
  updateJSON<T = unknown>(key: string, mutate: (current: T | undefined) => T, options?: UpdateJSONOptions): Promise<JSONResult<T>>;
}

const USERDATA_UNAUTH_MESSAGE =
  "window.backlit.userdata.* requires a signed-in user. " +
  "Call window.backlit.auth.me() to check first; an anonymous or signed-out " +
  "visitor cannot use this surface.";

function rewriteUnauth(e: unknown): never {
  if (e instanceof BacklitError && e.code === "unauthenticated") {
    throw new BacklitError("unauthenticated", USERDATA_UNAUTH_MESSAGE, e.status);
  }
  throw e;
}

export function createUserData(): UserDataNamespace {
  // const ns so the JSON methods compose the rewrite-wrapped base methods
  // (ns.get/ns.put/ns.putIfMatch) — they inherit the unauthenticated
  // rewrite without repeating the try/catch.
  const ns: UserDataNamespace = {
    async get(key: string): Promise<BlobResult> {
      validateDataKey(key);
      try {
        return await getBackend().userdataGet(key);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async put(key: string, body: BodyInit, contentType: string): Promise<PutResult> {
      validateDataKey(key);
      const norm = normalizeBody(body, contentType);
      try {
        return await getBackend().userdataPut(key, norm.body, norm.contentType);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async putIfMatch(
      key: string,
      body: BodyInit,
      contentType: string,
      expectedCrc32c: string,
    ): Promise<PutIfMatchResult> {
      validateDataKey(key);
      if (typeof expectedCrc32c !== "string") {
        throw new BacklitError("invalid_argument", "expectedCrc32c must be a string");
      }
      const norm = normalizeBody(body, contentType);
      try {
        // A { matched: false } witness returns through the try untouched;
        // only a thrown unauthenticated is rewritten by the catch.
        return await getBackend().userdataPutIfMatch(key, norm.body, norm.contentType, expectedCrc32c);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async delete(key: string): Promise<void> {
      validateDataKey(key);
      try {
        return await getBackend().userdataDelete(key);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async list(prefix: string = "", opts?: ListOptions): Promise<ListPage<ListEntry>> {
      validateDataPrefix(prefix);
      try {
        return await getBackend().userdataList(prefix, opts);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async getBatch(prefix: string = "", opts?: GetBatchOptions): Promise<ListPage<BatchEntry>> {
      validateDataPrefix(prefix);
      try {
        return await getBackend().userdataGetBatch(prefix, opts);
      } catch (e) {
        rewriteUnauth(e);
      }
    },

    async getJSON<T = unknown>(key: string): Promise<JSONResult<T>> {
      const r = await ns.get(key);
      return carryTags(r, { value: await decodeJSON<T>(r.value, key), crc32c: r.crc32c });
    },

    async getBatchJSON<T = unknown>(prefix: string = "", opts?: GetBatchOptions): Promise<ListPage<BatchJSONEntry<T>>> {
      // ns.getBatch already applies the unauthenticated rewrite; the JSON parse
      // + degrade adds no new network path.
      return decodeBatchJSONPage<T, BatchEntry>(await ns.getBatch(prefix, opts));
    },

    async putJSON(key: string, value: unknown): Promise<PutResult> {
      return ns.put(key, encodeJSON(value), JSON_CONTENT_TYPE);
    },

    async putJSONIfMatch<T = unknown>(
      key: string,
      value: unknown,
      expectedCrc32c: string,
    ): Promise<PutJSONIfMatchResult<T>> {
      const r = await ns.putIfMatch(key, encodeJSON(value), JSON_CONTENT_TYPE, expectedCrc32c);
      if (r.matched) return carryTags(r, { matched: true as const, crc32c: r.crc32c });
      return {
        matched: false,
        witness: carryTags(r.witness, { value: await decodeJSON<T>(r.witness.value, key), crc32c: r.witness.crc32c }),
      };
    },

    updateJSON<T = unknown>(
      key: string,
      mutate: (current: T | undefined) => T,
      options?: UpdateJSONOptions,
    ): Promise<JSONResult<T>> {
      return runUpdateJSON<T>((k) => ns.getJSON<T>(k), (k, v, exp) => ns.putJSONIfMatch<T>(k, v, exp), key, mutate, options);
    },
  };

  return ns;
}
